/**
 * Vocabulary the database does not carry.
 *
 * `page_type_ref` has no bilingual labels, and neither do the `doc_type`,
 * `statement_type`, `assurance`, `doc_status` or `period` enums. So the app
 * owns them (§11.2).
 *
 * An unknown key falls back to the raw database value in a muted monospace
 * style rather than throwing -- the pipeline can add a page type any day,
 * and the viewer showing `page_type: related_party_table` verbatim is fine.
 * Silently rendering nothing is not.
 */
export type Bi = { en: string; ar: string }
export type Vocab = Record<string, Bi>

/** The 39 rows of parser.page_type_ref, as of 2026-08-21. */
export const pageType: Vocab = {
  // statement
  stmt_financial_position:   { en: 'Statement of financial position', ar: 'قائمة المركز المالي' },
  stmt_income:               { en: 'Statement of income',             ar: 'قائمة الدخل' },
  stmt_comprehensive_income: { en: 'Statement of comprehensive income', ar: 'قائمة الدخل الشامل' },
  stmt_changes_equity:       { en: 'Statement of changes in equity',  ar: 'قائمة التغيرات في حقوق الملكية' },
  stmt_cash_flows:           { en: 'Statement of cash flows',         ar: 'قائمة التدفقات النقدية' },
  multi_year_summary:        { en: 'Multi-year summary',              ar: 'ملخص متعدد السنوات' },
  // note
  note_mixed:                { en: 'Note — text and table',           ar: 'إيضاح — نص وجدول' },
  note_narrative:            { en: 'Note — text',                     ar: 'إيضاح — نص' },
  note_table:                { en: 'Note — table',                    ar: 'إيضاح — جدول' },
  // auditor
  auditor_report:            { en: "Independent auditor's report",    ar: 'تقرير المراجع المستقل' },
  auditor_kam:               { en: 'Key audit matters',               ar: 'أمور المراجعة الرئيسية' },
  auditor_signature:         { en: "Auditor's signature",             ar: 'توقيع المراجع' },
  // metadata
  cover:                     { en: 'Cover',                           ar: 'الغلاف' },
  filing_details:            { en: 'Filing details',                  ar: 'بيانات التقرير' },
  contact_page:              { en: 'Contact details',                 ar: 'بيانات الاتصال' },
  // toc
  toc:                       { en: 'Contents',                        ar: 'المحتويات' },
  index:                     { en: 'Index',                           ar: 'الفهرس' },
  // roster
  board_roster:              { en: 'Board roster',                    ar: 'أعضاء مجلس الإدارة' },
  committee_roster:          { en: 'Committee roster',                ar: 'أعضاء اللجان' },
  meeting_attendance:        { en: 'Meeting attendance',              ar: 'حضور الاجتماعات' },
  ownership_table:           { en: 'Ownership',                       ar: 'الملكية' },
  penalties_table:           { en: 'Penalties',                       ar: 'العقوبات والجزاءات' },
  related_party_table:       { en: 'Related parties',                 ar: 'الأطراف ذات العلاقة' },
  remuneration_table:        { en: 'Remuneration',                    ar: 'المكافآت والتعويضات' },
  subsidiaries_table:        { en: 'Subsidiaries',                    ar: 'الشركات التابعة' },
  // prose
  board_declarations:        { en: 'Board declarations',              ar: 'إقرارات مجلس الإدارة' },
  dividend_policy:           { en: 'Dividend policy',                 ar: 'سياسة توزيع الأرباح' },
  esg_narrative:             { en: 'Sustainability narrative',        ar: 'سرد الاستدامة' },
  governance_narrative:      { en: 'Governance narrative',            ar: 'سرد الحوكمة' },
  legal_articles:            { en: 'Articles of association',         ar: 'النظام الأساسي' },
  mda_narrative:             { en: "Management's discussion",         ar: 'مناقشة الإدارة وتحليلها' },
  // metrics
  chart_infographic:         { en: 'Chart or infographic',            ar: 'رسم بياني' },
  esg_metrics:               { en: 'Sustainability metrics',          ar: 'مؤشرات الاستدامة' },
  operational_stats:         { en: 'Operational statistics',          ar: 'إحصاءات تشغيلية' },
  // skip
  back_cover:                { en: 'Back cover',                      ar: 'الغلاف الخلفي' },
  blank:                     { en: 'Blank',                           ar: 'صفحة فارغة' },
  decorative:                { en: 'Decorative',                      ar: 'صفحة زخرفية' },
  photo_gallery:             { en: 'Photo gallery',                   ar: 'معرض الصور' },
  section_divider:           { en: 'Section divider',                 ar: 'فاصل قسم' },
}

/** Short forms for the ~64px page rail, where the full label will not fit. */
export const pageTypeShort: Vocab = {
  stmt_financial_position:   { en: 'Fin. position',  ar: 'المركز المالي' },
  stmt_income:               { en: 'Income',         ar: 'الدخل' },
  stmt_comprehensive_income: { en: 'Compr. income',  ar: 'الدخل الشامل' },
  stmt_changes_equity:       { en: 'Equity',         ar: 'حقوق الملكية' },
  stmt_cash_flows:           { en: 'Cash flows',     ar: 'التدفقات' },
  multi_year_summary:        { en: 'Multi-year',     ar: 'متعدد السنوات' },
  note_mixed:                { en: 'Note ±tbl',      ar: 'إيضاح ±جدول' },
  note_narrative:            { en: 'Note',           ar: 'إيضاح' },
  note_table:                { en: 'Note tbl',       ar: 'جدول إيضاح' },
  auditor_report:            { en: 'Auditor',        ar: 'المراجع' },
  auditor_kam:               { en: 'KAM',            ar: 'أمور المراجعة' },
  auditor_signature:         { en: 'Signature',      ar: 'التوقيع' },
  cover:                     { en: 'Cover',          ar: 'الغلاف' },
  filing_details:            { en: 'Details',        ar: 'البيانات' },
  contact_page:              { en: 'Contact',        ar: 'الاتصال' },
  toc:                       { en: 'Contents',       ar: 'المحتويات' },
  index:                     { en: 'Index',          ar: 'الفهرس' },
  board_roster:              { en: 'Board',          ar: 'المجلس' },
  committee_roster:          { en: 'Committees',     ar: 'اللجان' },
  meeting_attendance:        { en: 'Attendance',     ar: 'الحضور' },
  ownership_table:           { en: 'Ownership',      ar: 'الملكية' },
  penalties_table:           { en: 'Penalties',      ar: 'العقوبات' },
  related_party_table:       { en: 'Related',        ar: 'الأطراف' },
  remuneration_table:        { en: 'Remuneration',   ar: 'المكافآت' },
  subsidiaries_table:        { en: 'Subsidiaries',   ar: 'التابعة' },
  board_declarations:        { en: 'Declarations',   ar: 'الإقرارات' },
  dividend_policy:           { en: 'Dividends',      ar: 'الأرباح' },
  esg_narrative:             { en: 'ESG text',       ar: 'الاستدامة' },
  governance_narrative:      { en: 'Governance',     ar: 'الحوكمة' },
  legal_articles:            { en: 'Articles',       ar: 'النظام' },
  mda_narrative:             { en: 'MD&A',           ar: 'مناقشة الإدارة' },
  chart_infographic:         { en: 'Chart',          ar: 'رسم بياني' },
  esg_metrics:               { en: 'ESG data',       ar: 'مؤشرات' },
  operational_stats:         { en: 'Ops stats',      ar: 'إحصاءات' },
  back_cover:                { en: 'Back',           ar: 'الخلفي' },
  blank:                     { en: 'Blank',          ar: 'فارغة' },
  decorative:                { en: 'Decorative',     ar: 'زخرفية' },
  photo_gallery:             { en: 'Photos',         ar: 'صور' },
  section_divider:           { en: 'Divider',        ar: 'فاصل' },
}

export const statementType: Vocab = {
  financial_position:   { en: 'Financial position',    ar: 'المركز المالي' },
  income:               { en: 'Income',                ar: 'الدخل' },
  comprehensive_income: { en: 'Comprehensive income',  ar: 'الدخل الشامل' },
  changes_equity:       { en: 'Changes in equity',     ar: 'التغيرات في حقوق الملكية' },
  cash_flows:           { en: 'Cash flows',            ar: 'التدفقات النقدية' },
  multi_year_summary:   { en: 'Multi-year summary',    ar: 'ملخص متعدد السنوات' },
}

export const docType: Vocab = {
  financial_statements: { en: 'Financial statements',  ar: 'القوائم المالية' },
  board_report:         { en: 'Board report',          ar: 'تقرير مجلس الإدارة' },
  annual_report:        { en: 'Annual report',         ar: 'التقرير السنوي' },
  esg_report:           { en: 'Sustainability report', ar: 'تقرير الاستدامة' },
  bylaws:               { en: 'Bylaws',                ar: 'النظام الأساسي' },
  other:                { en: 'Other',                 ar: 'أخرى' },
}

export const assurance: Vocab = {
  audited:   { en: 'Audited',   ar: 'مُراجعة' },
  reviewed:  { en: 'Reviewed',  ar: 'مفحوصة' },
  unaudited: { en: 'Unaudited', ar: 'غير مراجعة' },
}

export const docStatus: Vocab = {
  ingested:     { en: 'Ingested',     ar: 'مُستوردة' },
  probed:       { en: 'Probed',       ar: 'مُفحوصة بنيوياً' },
  classified:   { en: 'Classified',   ar: 'مُصنَّفة' },
  extracted:    { en: 'Extracted',    ar: 'مُستخرَجة' },
  normalized:   { en: 'Normalized',   ar: 'مُوحَّدة' },
  verified:     { en: 'Verified',     ar: 'مُتحقَّق منها' },
  needs_review: { en: 'Needs review', ar: 'تحتاج مراجعة' },
  failed:       { en: 'Failed',       ar: 'فشلت' },
}

/** documents.basis -- what the numbers cover. 136 consolidated, 95 standalone. */
export const basis: Vocab = {
  standalone:   { en: 'Standalone',   ar: 'منفصلة' },
  consolidated: { en: 'Consolidated', ar: 'موحّدة' },
}

/**
 * documents.rounding -- the scale the filing prints at. 54 of the 231
 * complete documents report in thousands, and reading one of those as units
 * is a thousandfold error, so it is shown rather than left implicit.
 */
export const rounding: Vocab = {
  units:     { en: 'in units',     ar: 'بالوحدات' },
  thousands: { en: 'in thousands', ar: 'بالآلاف' },
  millions:  { en: 'in millions',  ar: 'بالملايين' },
}

export const archetype: Vocab = {
  annual_audited:    { en: 'Annual, audited',    ar: 'سنوية مُراجعة' },
  interim_condensed: { en: 'Interim, condensed', ar: 'أولية موجزة' },
}

export const period: Vocab = {
  annual: { en: 'Annual', ar: 'سنوي' },
  q1:     { en: 'Q1',     ar: 'الربع الأول' },
  q2:     { en: 'Q2',     ar: 'الربع الثاني' },
  q3:     { en: 'Q3',     ar: 'الربع الثالث' },
  q4:     { en: 'Q4',     ar: 'الربع الرابع' },
}

export const periodShort: Vocab = {
  annual: { en: 'FY', ar: 'سنوي' },
  q1:     { en: 'Q1', ar: 'ر١' },
  q2:     { en: 'Q2', ar: 'ر٢' },
  q3:     { en: 'Q3', ar: 'ر٣' },
  q4:     { en: 'Q4', ar: 'ر٤' },
}

export const extractor: Vocab = {
  skip:      { en: 'Skipped',    ar: 'متجاوَزة' },
  metadata:  { en: 'Metadata',   ar: 'بيانات وصفية' },
  toc:       { en: 'Contents',   ar: 'المحتويات' },
  auditor:   { en: 'Auditor',    ar: 'المراجع' },
  statement: { en: 'Statement',  ar: 'قائمة مالية' },
  note:      { en: 'Note',       ar: 'إيضاح' },
  roster:    { en: 'Roster',     ar: 'جدول أسماء' },
  prose:     { en: 'Prose',      ar: 'نص' },
  metrics:   { en: 'Metrics',    ar: 'مؤشرات' },
}

export const locale: Vocab = {
  ar: { en: 'Arabic',  ar: 'العربية' },
  en: { en: 'English', ar: 'الإنجليزية' },
}

export const industry: Vocab = {
  cement: { en: 'Cement', ar: 'الأسمنت' },
}

/**
 * Check names from parser.validations. Not exhaustive by design -- a check
 * the pipeline adds next month must not be invisible here, so an unknown
 * name renders as itself.
 */
export const checkName: Vocab = {
  // The six names parser.validations actually carries, plus the ones the
  // pipeline's own §5 phase 11 describes but that this corpus has not
  // produced yet. An unknown name still renders as itself.
  total_equals_sum_of_children:        { en: 'Total equals the sum of its children',      ar: 'الإجمالي يساوي مجموع بنوده' },
  assets_equal_liabilities_plus_equity:{ en: 'Assets = liabilities + equity',             ar: 'الموجودات = المطلوبات + حقوق الملكية' },
  note_refs_resolve:                   { en: 'Every note reference resolves',             ar: 'كل إحالة إلى إيضاح لها ما يقابلها' },
  xbrl_diff:                           { en: 'Agreement with the XBRL sibling',           ar: 'التطابق مع ملف XBRL' },
  prior_period_matches_earlier_filing: { en: 'Prior-period column matches the earlier filing', ar: 'عمود الفترة السابقة يطابق الإفصاح الأسبق' },
  net_income_agrees_across_statements: { en: 'Net income agrees across statements',       ar: 'صافي الدخل متسق بين القوائم' },
  equity_closing_opens_next:           { en: 'Equity closing balance opens the next period', ar: 'رصيد حقوق الملكية الختامي يفتتح الفترة التالية' },
  remuneration_row_sums:               { en: 'Remuneration row equals its components',    ar: 'صف المكافآت يساوي مكوناته' },
}

/**
 * Reasons in parser.review_queue that are not derived from a check. The
 * check-derived ones arrive as "error: <check_name>" and are stripped to
 * their check name server-side.
 */
export const reviewReason: Vocab = {
  pages_never_extracted: { en: 'Pages that were never extracted', ar: 'صفحات لم تُستخرَج إطلاقاً' },
  extraction_failed:     { en: 'Extraction failed',               ar: 'فشل الاستخراج' },
}

/**
 * `board_memberships.body` and `remuneration.body`. The pipeline writes these
 * from the roster prompt, so the list grows with the corpus -- an unknown one
 * still renders as itself.
 */
export const body: Vocab = {
  board:                { en: 'Board',                        ar: 'المجلس' },
  audit_committee:      { en: 'Audit committee',              ar: 'لجنة المراجعة' },
  nomination_committee: { en: 'Nomination committee',         ar: 'لجنة الترشيحات' },
  remuneration_committee: { en: 'Remuneration committee',     ar: 'لجنة المكافآت' },
  executive_committee:  { en: 'Executive committee',          ar: 'اللجنة التنفيذية' },
  risk_committee:       { en: 'Risk committee',               ar: 'لجنة المخاطر' },
  executive_management: { en: 'Executive management',         ar: 'الإدارة التنفيذية' },
  other:                { en: 'Other',                        ar: 'أخرى' },
}

export const VOCABS = {
  pageType, pageTypeShort, statementType, docType, assurance,
  docStatus, period, periodShort, extractor, locale, industry, checkName,
  basis, rounding, archetype,
  reviewReason, body,
} as const

export type VocabName = keyof typeof VOCABS
